import React, { useContext, useState } from "react";

import { SettingsOutlined } from "@mui/icons-material";
import { Button, MenuItem } from "@mui/material";
import { Link, useLocation } from "react-router-dom";

import { SettingValue } from "@safetyculture/s12-apis-connect-web/s12/accounts/settings/v1/settings_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { OrgSettingsDialog } from "../org-settings/org-settings-dialog";
import UpdateOrgEmailModal from "../update-org-domain-dialog";
import { UserFieldsDialog } from "../user-fields/user-fields-dialog";
import KebabMenu from "components/shared/kebab-menu";
import { OrgDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";

export interface OrgActionsMenuProps {
  regionCode: string;
  orgSettings: SettingValue[];
  /**
   * Turns card editing on and off. It lives at the bottom of this menu rather
   * than behind a gear of its own: it is a rare action, and a second icon in the
   * strip earns less than the space it costs.
   */
  onToggleCustomiseLayout?: () => void;
  customisingLayout?: boolean;
}

/**
 * The organisation-wide actions that used to live only in the detail pane's
 * header kebab: domain updates, retroshare, sites, templates, GRS, user fields,
 * settings and custom labels.
 *
 * Extracted (FG-8053) because the pane is no longer always on screen - the
 * dashboard's default layout shows focused cards instead - and these actions
 * are about the organisation rather than about any one card, so they belong
 * beside the org name in the summary strip. The pane keeps rendering it too,
 * from this one implementation.
 */
const OrgActionsMenu = ({
  regionCode,
  orgSettings,
  onToggleCustomiseLayout,
  customisingLayout,
}: OrgActionsMenuProps) => {
  const location = useLocation();
  const { orgData } = useContext(OrgDataContext);
  const { hasPermissions } = useContext(PermissionsContext);

  const [updateEmailModalOpen, setUpdateEmailModalOpen] = useState(false);
  const [userFieldModalOpen, setUserFieldModalOpen] = useState(false);
  const [orgSettingsModalOpen, setOrgSettingsModalOpen] = useState(false);

  const canManageCustomLabels = hasPermissions([
    ActionType.LIST_WHITE_LABEL_REPLACEMENTS,
    ActionType.CREATE_WHITE_LABEL_REPLACEMENT,
    ActionType.UPDATE_WHITE_LABEL_REPLACEMENT,
    ActionType.DELETE_WHITE_LABEL_REPLACEMENT,
  ]);

  return (
    <>
      <KebabMenu>
        <MenuItem>
          <Button
            style={{
              padding: "0",
            }}
            color="primary"
            data-testid="organisation-domain-update-btn"
            onClick={() => {
              setUpdateEmailModalOpen(true);
            }}
          >
            Update Domain
          </Button>
        </MenuItem>
        <MenuItem>
          <Link
            to={{
              pathname: "/search/retroshare",
              state: {
                orgId: orgData.id,
                regionCode,
              },
            }}
            className="remove-link-style"
          >
            <Button
              style={{
                padding: "0",
              }}
              color="primary"
              data-testid="retroshare-btn"
            >
              Retroshare Tool
            </Button>
          </Link>
        </MenuItem>
        <MenuItem>
          <Link
            to={{
              pathname: `${location.pathname}/sites`,
              state: {
                orgName: orgData.name,
              },
            }}
            className="remove-link-style"
          >
            <Button
              style={{
                padding: "0",
                textTransform: "none",
              }}
              color="primary"
              data-testid="organisation-sites-btn"
            >
              ORGANISATION SITES
            </Button>
          </Link>
        </MenuItem>
        <MenuItem>
          <a
            href={`/search/template?roleId=${orgData.id}`}
            className="remove-link-style"
            data-testid="organisation-templates-link"
          >
            <Button
              style={{
                padding: "0",
                textTransform: "none",
              }}
              color="primary"
              data-testid="organisation-templates-btn"
            >
              ORGANISATION TEMPLATES
            </Button>
          </a>
        </MenuItem>
        <MenuItem>
          <a
            href={`/search/grs?roleId=${orgData.id}`}
            className="remove-link-style"
            data-testid="organisation-grs-link"
          >
            <Button
              style={{
                padding: "0",
                textTransform: "none",
              }}
              color="primary"
              data-testid="organisation-grs-btn"
            >
              ORGANISATION GRS
            </Button>
          </a>
        </MenuItem>
        <MenuItem>
          <Button
            style={{
              padding: "0",
              textTransform: "none",
            }}
            color="primary"
            onClick={() => setUserFieldModalOpen(true)}
            data-testid="user-fields-btn"
          >
            USER FIELDS
          </Button>
        </MenuItem>
        <MenuItem>
          <Button
            style={{
              padding: "0",
              textTransform: "none",
            }}
            color="primary"
            onClick={() => setOrgSettingsModalOpen(true)}
            data-testid="org-settings-btn"
          >
            ORGANIZATION SETTINGS
          </Button>
        </MenuItem>
        <MenuItem disabled={!canManageCustomLabels}>
          <Link
            to={{
              pathname: `${location.pathname}/custom-labels`,
              state: {
                orgName: orgData.name,
              },
            }}
            className="remove-link-style"
            data-testid="org-custom-labels-link"
          >
            <Button
              style={{
                padding: "0",
                textTransform: "none",
              }}
              color="primary"
              data-testid="organisation-custom-labels-btn"
            >
              CUSTOM LABELS
            </Button>
          </Link>
        </MenuItem>
        {onToggleCustomiseLayout ? (
          <MenuItem divider={false} onClick={onToggleCustomiseLayout}>
            <Button
              style={{
                padding: "0",
                textTransform: "none",
              }}
              color="primary"
              startIcon={<SettingsOutlined fontSize="small" />}
              data-testid="org-customise-layout-btn"
            >
              {customisingLayout ? "DONE CUSTOMISING" : "CUSTOMISE LAYOUT"}
            </Button>
          </MenuItem>
        ) : null}
      </KebabMenu>
      <UserFieldsDialog
        open={userFieldModalOpen}
        setIsOpen={setUserFieldModalOpen}
        roleId={orgData.id}
        regionCode={regionCode}
      />
      <OrgSettingsDialog
        open={orgSettingsModalOpen}
        setIsOpen={setOrgSettingsModalOpen}
        orgSettings={orgSettings}
      />
      <UpdateOrgEmailModal
        open={updateEmailModalOpen}
        setOpen={setUpdateEmailModalOpen}
        roleId={orgData.id}
        regionCode={regionCode}
      />
    </>
  );
};

export default OrgActionsMenu;
